import {
  type FieldPath,
  type FieldValues,
  Controller as RHController,
  type ControllerProps as RHControllerProps,
} from "react-hook-form";
import { Input, type InputProps } from "./Input";
import type React from "react";
import { pascalToWords } from "@dmca/utils/functions";

export interface ControllerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues
> extends Omit<
    RHControllerProps<TFieldValues, TName, TTransformedValues>,
    "render"
  > {
  /**
   * Props to pass to the default Input component. Has no effect if `render` is specified.
   */
  inputProps?: InputProps;
  /**
   * The controller renders an Input field by default. Use this to change the rendered component.
   */
  render?: RHControllerProps<TFieldValues, TName, TTransformedValues>["render"];
  /**
   * Transform the input value before it is passed to the controller. Has no effect if `render` is specified.
   */
  transform?:
    | "number"
    | {
        /**
         * Transform from the actual value to the value displayed on the input.
         */
        input: (value: any) => string;
        /**
         * Transform from the value entered on the input to the actual value.
         */
        output: (e: React.ChangeEvent<any>) => any;
      };
}

const defaultTransform = {
  number: {
    input: (value: number) => {
      return value === undefined || value === null ? "" : value.toString();
    },
    output: (e: React.ChangeEvent<any>) => {
      const output = parseFloat(e.target.value);
      console.log("Parsed number:", output);
      return isNaN(output) ? null : output;
    },
  },
};

export function Controller<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues
>({
  render,
  inputProps,
  transform,
  ...rest
}: ControllerProps<TFieldValues, TName, TTransformedValues>): React.ReactNode {
  const transformInput = transform
    ? typeof transform === "string"
      ? defaultTransform[transform].input
      : transform.input
    : undefined;
  const transformOutput = transform
    ? typeof transform === "string"
      ? defaultTransform[transform].output
      : transform.output
    : undefined;
  return (
    <RHController
      {...rest}
      render={
        render ??
        (({ field: { onChange, value, ...field }, fieldState }) => {
          return (
            <Input
              {...field}
              value={transformInput ? transformInput(value as never) : value}
              error={!!fieldState.error}
              errorText={
                fieldState.error?.message ??
                (fieldState.error as any)?.map((x: any) => x.message).join(", ")
              }
              required={!!rest.rules?.required}
              label={pascalToWords(field.name)}
              {...inputProps}
              onChange={(e) => {
                onChange(transformOutput ? transformOutput(e) : e);
                inputProps?.onChange?.(e);
              }}
            />
          );
        })
      }
    />
  );
}
